import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getProfileImageUrl } from "../../utils/imageUtils";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt,
  FaComments,
  FaStar,
  FaCheckCircle,
  FaCalendarAlt,
  FaBox,
  FaHeart,
  FaArrowLeft,
  FaEnvelope,
  FaUserShield,
} from "react-icons/fa";
import "./UserProfile.css";

const UserProfile = () => {
  const { t } = useTranslation();
  const { username } = useParams();
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch {
        /* ignore */
      }
    }
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!username) {
        setError("No username provided");
        setLoading(false);
        return;
      }

      const res = await api.get(`/users/${encodeURIComponent(username)}`);
      const userData = res.data;
      if (!userData?._id) {
        setError("User not found");
        setLoading(false);
        return;
      }
      if (!userData.username) userData.username = username;

      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const p = JSON.parse(storedUser);
          if (p.id === userData._id || p._id === userData._id) {
            navigate(
              userData.role === "farmer"
                ? "/farmer/profile"
                : "/consumer/profile",
            );
            setLoading(false);
            return;
          }
        } catch {
          /* continue */
        }
      }

      if (userData.role === "farmer") {
        navigate(`/farmers/${userData.username}`, { replace: true });
        setLoading(false);
        return;
      }

      let totalOrders = 0,
        completedOrders = 0,
        totalReviews = 0,
        averageRatingGiven = 0;
      try {
        const ordRes = await api.get("/orders");
        const consumerOrders = (ordRes.data || []).filter(
          (o) =>
            (o.consumer?._id || o.consumer)?.toString() ===
            userData._id.toString(),
        );
        totalOrders = consumerOrders.length;
        completedOrders = consumerOrders.filter((o) =>
          ["delivered", "received", "completed"].includes(
            o.orderStatus || o.status,
          ),
        ).length;
      } catch {
        /* ignore */
      }

      try {
        const revRes = await api.get("/reviews");
        const consumerReviews = (revRes.data || []).filter(
          (r) =>
            (r.consumer?._id || r.consumer)?.toString() ===
            userData._id.toString(),
        );
        totalReviews = consumerReviews.length;
        if (consumerReviews.length > 0) {
          averageRatingGiven =
            consumerReviews.reduce((s, r) => s + (r.rating || 0), 0) /
            consumerReviews.length;
        }
      } catch {
        /* ignore */
      }

      setProfileUser({
        ...userData,
        username: userData.username || username,
        district: userData.address?.district || "",
        province: userData.address?.province || "",
        city: userData.address?.city || "",
        joinedDate: userData.createdAt || new Date(),
        totalOrders,
        completedOrders,
        totalReviews,
        averageRatingGiven: Math.round(averageRatingGiven * 10) / 10,
      });
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load profile");
      setLoading(false);
    }
  };

  const getProfileImage = () => {
    if (profileUser?.profileImage) {
      return profileUser.profileImage.startsWith("http")
        ? profileUser.profileImage
        : getProfileImageUrl(profileUser.profileImage);
    }
    return "/images/deafultpic.png";
  };

  const getLocationString = () => {
    const parts = [
      profileUser.city,
      profileUser.district,
      profileUser.province,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getJoinedLabel = () => {
    const d = new Date(profileUser.joinedDate);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="cup-page">
        <Header />
        <div className="cup-loading">
          <div className="cup-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("userProfile.loading")}</p>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="cup-page">
        <Header />
        <div className="cup-not-found">
          <FaUserShield className="cup-nf-icon" />
          <h2>{t("userProfile.notFound")}</h2>
          <p>{error || t("userProfile.notFoundMessage")}</p>
          <Link to="/" className="cup-nf-btn">
            <FaArrowLeft /> Go Home
          </Link>
        </div>
      </div>
    );
  }

  const location = getLocationString();

  return (
    <div className="cup-page">
      <Header />

      {/* Hero band */}
      <div className="cup-hero">
        <div className="cup-hero-inner">
          <div className="cup-avatar-ring">
            <img
              src={getProfileImage()}
              alt={profileUser.name}
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/deafultpic.png";
              }}
            />
            {profileUser.verified && (
              <span className="cup-avatar-badge">
                <FaCheckCircle />
              </span>
            )}
          </div>
          <h1 className="cup-name">{profileUser.name}</h1>

          {location && (
            <span className="cup-location">
              <FaMapMarkerAlt /> {location}
            </span>
          )}

          {/* Message CTA */}
          {currentUser &&
            currentUser._id !== profileUser._id &&
            currentUser.id !== profileUser._id && (
              <button
                className="cup-msg-btn"
                onClick={() =>
                  navigate(`/messages?consumer=${profileUser._id}`)
                }
              >
                <FaComments /> {t("userProfile.messageCustomer") || "Message"}
              </button>
            )}
          {!currentUser && (
            <Link to="/login" className="cup-msg-btn">
              <FaEnvelope />{" "}
              {t("userProfile.loginToMessageGeneric") || "Login to Message"}
            </Link>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="cup-content">
        {/* Stats */}
        <section className="cup-stats">
          <div className="cup-stat cup-stat--orders">
            <div className="cup-stat-icon">
              <FaBox />
            </div>
            <span className="cup-stat-num">{profileUser.totalOrders}</span>
            <span className="cup-stat-label">
              {t("userProfile.totalOrders") || "Orders"}
            </span>
          </div>
          <div className="cup-stat cup-stat--done">
            <div className="cup-stat-icon">
              <FaCheckCircle />
            </div>
            <span className="cup-stat-num">{profileUser.completedOrders}</span>
            <span className="cup-stat-label">
              {t("userProfile.completed") || "Completed"}
            </span>
          </div>
          <div className="cup-stat cup-stat--reviews">
            <div className="cup-stat-icon">
              <FaHeart />
            </div>
            <span className="cup-stat-num">{profileUser.totalReviews}</span>
            <span className="cup-stat-label">Reviews</span>
          </div>
          <div className="cup-stat cup-stat--joined">
            <div className="cup-stat-icon">
              <FaCalendarAlt />
            </div>
            <span className="cup-stat-num">{getJoinedLabel()}</span>
            <span className="cup-stat-label">
              {t("userProfile.memberSince") || "Member Since"}
            </span>
          </div>
        </section>

        {/* Average Rating Given */}
        {profileUser.totalReviews > 0 && (
          <section className="cup-rating-card">
            <div className="cup-rating-left">
              <span className="cup-rating-big">
                {profileUser.averageRatingGiven.toFixed(1)}
              </span>
              <div className="cup-stars">
                {[1, 2, 3, 4, 5].map((i) => (
                  <FaStar
                    key={i}
                    className={
                      i <= Math.round(profileUser.averageRatingGiven)
                        ? "cup-star--on"
                        : "cup-star--off"
                    }
                  />
                ))}
              </div>
            </div>
            <div className="cup-rating-right">
              <span className="cup-rating-label">Average Rating Given</span>
              <span className="cup-rating-sub">
                Based on {profileUser.totalReviews} review
                {profileUser.totalReviews > 1 ? "s" : ""}
              </span>
            </div>
          </section>
        )}

        {/* About card */}
        <section className="cup-about">
          <h3>About</h3>
          <div className="cup-about-grid">
            {location && (
              <div className="cup-about-item">
                <FaMapMarkerAlt className="cup-about-icon" />
                <div>
                  <span className="cup-about-key">Location</span>
                  <span className="cup-about-val">{location}</span>
                </div>
              </div>
            )}
            <div className="cup-about-item">
              <FaCalendarAlt className="cup-about-icon" />
              <div>
                <span className="cup-about-key">Joined</span>
                <span className="cup-about-val">{getJoinedLabel()}</span>
              </div>
            </div>
            {profileUser.verified && (
              <div className="cup-about-item">
                <FaCheckCircle className="cup-about-icon cup-about-icon--green" />
                <div>
                  <span className="cup-about-key">Status</span>
                  <span className="cup-about-val">Verified</span>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserProfile;
